import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import { STATUS_COLORS } from './utils/MetadataUtils';

type FontStatus = {
  font: string;
  matched?: boolean;
  present?: boolean;
  key: string;
};

type ParsedFonts = {
  actualFonts: string[];
  requiredFonts: string[];
  optionalFonts: string[];
  expectedUnknown: boolean;
};

type Props = {
  parsedFonts: ParsedFonts;
  rawRule?: string;
};

const FontCheckResult = ({ parsedFonts, rawRule }: Props) => {
  const { actualFonts, requiredFonts, optionalFonts, expectedUnknown } = parsedFonts;
  const optionalMissingColor = '#9e9e9e';

  const actualCounts = actualFonts.reduce<Record<string, number>>((acc, font) => {
    acc[font] = (acc[font] || 0) + 1;
    return acc;
  }, {});
  const originalActualCounts = { ...actualCounts };

  const requiredStatus: FontStatus[] = requiredFonts.map((font, idx) => {
    const available = actualCounts[font] || 0;
    const matched = available > 0;
    if (matched) {
      actualCounts[font] = available - 1;
    }
    return { font, matched, key: `${font}-${idx}` };
  });

  const optionalStatus: FontStatus[] = optionalFonts.map((font, idx) => {
    const present = (originalActualCounts[font] || 0) > 0;
    return { font, present, key: `${font}-${idx}` };
  });
  const actualFontCounts = actualFonts.reduce<Record<string, number>>((acc, font) => {
    acc[font] = (acc[font] || 0) + 1;
    return acc;
  }, {});

  const requiredSet = new Set(requiredFonts);
  const optionalSet = new Set(optionalFonts);
  const unexpectedCounts = actualFonts
    .filter(font => !requiredSet.has(font) && !optionalSet.has(font))
    .reduce<Record<string, number>>((acc, font) => {
      acc[font] = (acc[font] || 0) + 1;
      return acc;
    }, {});

  return (
    <div>
      {expectedUnknown && (
        <div className="mb-1">
          <div style={{ fontSize: '10px' }}>Font rule is not defined.</div>
        </div>
      )}

      {!expectedUnknown && (
        <div className="mb-1">
          <div style={{ fontSize: '9px', color: '#000' }}>Required Fonts ({requiredStatus.length})</div>
          {requiredStatus.length === 0 && (
            <div>
              <span style={{ color: optionalMissingColor, marginLeft: '20px' }}>{'<none>'}</span>
            </div>
          )}
          {requiredStatus.map(itemStatus => (
            <div className="d-flex align-items-center" key={itemStatus.key}>
              {itemStatus.matched
                ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: STATUS_COLORS.PASS, marginRight: '4px' }} />
                : <ReportProblemOutlinedIcon fontSize="small" sx={{ color: STATUS_COLORS.FAIL, marginRight: '4px' }} />}
              <span style={{ color: itemStatus.matched ? STATUS_COLORS.PASS : STATUS_COLORS.FAIL }}>{itemStatus.font}</span>
            </div>
          ))}
        </div>
      )}

      {!expectedUnknown && (
        <div className="mb-1">
          <div style={{ fontSize: '9px', color: '#000' }}>Optional Fonts ({optionalStatus.length})</div>
          {optionalStatus.length === 0 && (
            <div>
              <span style={{ color: optionalMissingColor, marginLeft: '20px' }}>{'<none>'}</span>
            </div>
          )}
          {optionalStatus.map(itemStatus => {
            const isPresent = !!itemStatus.present;
            const color = optionalMissingColor;
            const Icon = isPresent ? AddCircleOutlineIcon : RemoveCircleOutlineIcon;
            return (
              <div className="d-flex align-items-center" key={itemStatus.key}>
                <Icon fontSize="small" sx={{ color, marginRight: '4px' }} />
                <span style={{ color }}>{itemStatus.font}</span>
              </div>
            );
          })}
        </div>
      )}

      {!expectedUnknown && (
        <div>
          <div style={{ fontSize: '9px', color: '#000' }}>Unexpected Fonts ({Object.keys(unexpectedCounts).length})</div>
          {Object.keys(unexpectedCounts).length === 0 && (
            <div>
              <span style={{ color: optionalMissingColor, marginLeft: '20px' }}>{'<none>'}</span>
            </div>
          )}
          {Object.entries(unexpectedCounts).map(([font, count]) => {
            const occurrences = Number(count) || 0;
            return (
              <div className="d-flex align-items-center" key={`unexpected-${font}`}>
                <ReportProblemOutlinedIcon fontSize="small" sx={{ color: STATUS_COLORS.FAIL, marginRight: '4px' }} />
                <span style={{ color: STATUS_COLORS.FAIL }}>{font}{occurrences > 1 ? ` (x${occurrences})` : ''}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-1">
        <div style={{ fontSize: '9px', color: '#000' }}>All fonts in document ({Object.keys(actualFontCounts).length})</div>
        {Object.keys(actualFontCounts).length === 0 && (
          <div>
            <span style={{ color: optionalMissingColor, marginLeft: '20px' }}>{'<none>'}</span>
          </div>
        )}
        {Object.entries(actualFontCounts).map(([font, count]) => (
          <div key={`actual-font-${font}`} className="d-flex align-items-center">
            <FiberManualRecordIcon fontSize="inherit" sx={{ color: '#000', fontSize: '0.6rem', marginRight: '4px' }} />
            <span style={{ color: '#000' }}>
              {font}{count > 1 ? ` (x${count})` : ''}
            </span>
          </div>
        ))}
      </div>

      {!expectedUnknown && (
        <div className="mt-1">
          <div style={{ fontSize: '9px', color: '#000' }}>Raw Font Rule</div>
          <div style={{ fontSize: '10px', color: '#000', wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
            {rawRule || '<none>'}
          </div>
        </div>
      )}
    </div>
  );
};

export default FontCheckResult;
